import el from "@cypherpotato/el";
import { api } from "../../../api";
import { AgenticTestResultBadge } from "../../../components/badge";
import { Button, ButtonRow, DangerButton } from "../../../components/button";
import { Card, CardNavigation } from "../../../components/card";
import { CommandBar } from "../../../components/command-bar";
import {
    ConversationInfoMessage,
    ConversationMessage
} from "../../../components/conversation";
import { Dashboard } from "../../../components/dashboard";
import { DateTimeLabel } from "../../../components/datetime-label";
import { Definition, DefinitionList } from "../../../components/definition";
import { Dropdown, DropdownItem } from "../../../components/dropdown";
import { Icon } from "../../../components/icon";
import { JsonControl } from "../../../components/json-control";
import { Padding } from "../../../components/layout";
import {
    Playground,
    PlaygroundAside,
    PlaygroundAsideContainer,
    PlaygroundContent,
    PlaygroundDefault
} from "../../../components/playground";
import {
    HorizontalSizableContainer,
    HorizontalSizableSection,
    SizableContainer,
    SizableSection
} from "../../../components/sizable-container";
import { TabControl } from "../../../components/tabcontrol";
import { CopiableText, MutedText, Paragraph } from "../../../components/text";
import { copyToClipboard } from "../../../ui/clipboard";
import { closeFlyout, showFlyout } from "../../../ui/flyout";
import { formatFlexibibleCurrency, formatFloat } from "../../../ui/format";
import { runOperation } from "../../../ui/operation";
import { cond } from "../../../util";

export default function (ctx) {
    const testId = ctx.params.id;
    const runId = ctx.params.runId;
    const run = el.state.create(null);
    const eventSource = api.createEventSource(`/api/v1/agentic-tests/${testId}/sse`);
    let disposed = false;
    let refreshVersion = 0;

    async function refreshRun() {
        const version = ++refreshVersion;
        const response = await api.getJson(`/api/v1/agentic-tests/${testId}/runs/${runId}`);
        if (!disposed && version === refreshVersion && response?.data) {
            run.value = response.data;
            if (["succeeded", "failed", "cancelled"].includes(run.value.state)) {
                eventSource.close();
            }
        }
    }

    eventSource.onmessage = event => {
        const payload = JSON.parse(event.data);
        if (payload.type === "agentic-test.run.updated" && payload.data?.run_id === runId) {
            refreshRun();
        }
    };

    runOperation("Loading Agentic Test run...", refreshRun);

    return Dashboard(
        {
            classList: ["full-size"],
            mount(element) {
                const observer = new MutationObserver(() => {
                    if (!element.isConnected) {
                        disposed = true;
                        eventSource.close();
                        observer.disconnect();
                    }
                });
                observer.observe(document.body, { childList: true, subtree: true });
            }
        },
        Card(
            CardNavigation(
                CommandBar(
                    ButtonRow(
                        Button(
                            Icon("arrow-left-line"),
                            "Back to test",
                            {
                                onClick() {
                                    history.go(-1);
                                }
                            }
                        )
                    ),
                    el.state.listening([run], () =>
                        ButtonRow(
                            ["pending", "running"].includes(
                                run.value?.state?.toLowerCase()
                            ) && DangerButton(
                                Icon("stop-circle-line"),
                                "Cancel run",
                                {
                                    onClick() {
                                        showFlyout(
                                            Paragraph(
                                                "Cancel this run? Partial execution data will be retained."
                                            ),
                                            DangerButton(
                                                "Cancel run",
                                                {
                                                    onClick(event) {
                                                        runOperation(
                                                            "Cancelling run...",
                                                            async () => {
                                                                await api.postJson(
                                                                    `/api/v1/agentic-tests/${testId}/runs/${runId}/cancel`,
                                                                    {}
                                                                );
                                                                closeFlyout(event.target);
                                                                run.value = (await api.getJson(
                                                                    `/api/v1/agentic-tests/${testId}/runs/${runId}`
                                                                )).data;
                                                            }
                                                        );
                                                    }
                                                }
                                            )
                                        );
                                    }
                                }
                            ),
                            run.value && Dropdown(
                                "Export",
                                DropdownItem(
                                    "Download JSON",
                                    Icon("download-line"),
                                    {
                                        onClick() {
                                            const blob = new Blob(
                                                [JSON.stringify(run.value, null, 2)],
                                                {
                                                    type: "application/json"
                                                }
                                            );
                                            const link = document.createElement("a");
                                            link.href = URL.createObjectURL(blob);
                                            link.download = `agentic-test-${testId}-run-${runId}.json`;
                                            link.click();
                                            URL.revokeObjectURL(link.href);
                                        }
                                    }
                                ),
                                DropdownItem(
                                    "Copy JSON",
                                    Icon("file-copy-line"),
                                    {
                                        onClick() {
                                            copyToClipboard(
                                                JSON.stringify(run.value, null, 2)
                                            );
                                        }
                                    }
                                )
                            )
                        )
                    )
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        {
                            initialSize: "400px"
                        },
                        PlaygroundAside(
                            el.state.listening([run], () =>
                                TabControl(
                                    [
                                        {
                                            title: "Run data",
                                            icon: Icon("list-unordered"),
                                            contents: PlaygroundAsideContainer(
                                                el.state.listening([run], () =>
                                                    SizableContainer(
                                                        SizableSection(
                                                            "Run data",
                                                            Padding(
                                                                0.5,
                                                                run.value
                                                                    ? DefinitionList(
                                                                        Definition(
                                                                            "ID",
                                                                            CopiableText(
                                                                                `#${run.value.id.slice(-4)}`,
                                                                                run.value.id
                                                                            )
                                                                        ),
                                                                        Definition(
                                                                            "Test",
                                                                            run.value.test_name
                                                                            || MutedText("Unnamed test")
                                                                        ),
                                                                        Definition(
                                                                            "Result",
                                                                            AgenticTestResultBadge(
                                                                                run.value
                                                                            )
                                                                        ),
                                                                        Definition(
                                                                            "Score",
                                                                            run.value.score != null
                                                                                ? formatFloat(run.value.score)
                                                                                : MutedText("-")
                                                                        ),
                                                                        Definition(
                                                                            "Cost",
                                                                            formatFlexibibleCurrency(
                                                                                run.value.cost || 0
                                                                            )
                                                                        ),
                                                                        Definition(
                                                                            "External user ID",
                                                                            run.value.external_user_id
                                                                                ? CopiableText(
                                                                                    run.value.external_user_id
                                                                                )
                                                                                : MutedText("Empty")
                                                                        )
                                                                    )
                                                                    : MutedText("Loading run...")
                                                            )
                                                        ),
                                                        SizableSection(
                                                            "Timestamps",
                                                            Padding(
                                                                0.5,
                                                                run.value
                                                                    ? DefinitionList(
                                                                        Definition(
                                                                            "Created",
                                                                            DateTimeLabel(
                                                                                run.value.created_at
                                                                            )
                                                                        ),
                                                                        Definition(
                                                                            "Started",
                                                                            run.value.started_at
                                                                                ? DateTimeLabel(
                                                                                    run.value.started_at
                                                                                )
                                                                                : MutedText("-")
                                                                        ),
                                                                        Definition(
                                                                            "Finished",
                                                                            run.value.finished_at
                                                                                ? DateTimeLabel(
                                                                                    run.value.finished_at
                                                                                )
                                                                                : MutedText("-")
                                                                        )
                                                                    )
                                                                    : MutedText("Loading run...")
                                                            )
                                                        ),
                                                        run.value?.error && SizableSection(
                                                            "Error",
                                                            Padding(
                                                                0.5,
                                                                Paragraph(run.value.error)
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        },
                                        {
                                            title: "Result",
                                            icon: Icon("braces-line"),
                                            contents: PlaygroundAsideContainer(
                                                el.state.listening([run], () =>
                                                    SizableContainer(
                                                        SizableSection(
                                                            "Result",
                                                            Padding(
                                                                0.5,
                                                                run.value
                                                                    ? JsonControl({
                                                                        value: run.value.result || {},
                                                                        readonly: true
                                                                    })
                                                                    : MutedText("Loading result...")
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        }
                                    ],
                                    {
                                        type: "vertical"
                                    }
                                )
                            )
                        )
                    ),
                    HorizontalSizableSection(
                        {
                            initialSize: "calc(100% - 400px)"
                        },
                        el.state.listening([run], () =>
                            PlaygroundContent(
                                !run.value
                                    ? PlaygroundDefault(
                                        "loader-4-line",
                                        "Loading run conversation..."
                                    )
                                    : run.value.conversation?.length
                                        ? run.value.conversation.map((message, index) =>
                                            ConversationMessage({
                                                role: message.role,
                                                tool_calls: message.tool_calls,
                                                tool_call_id: message.tool_call_id,
                                                name: message.name,
                                                content: message.role === "judge"
                                                    ? [
                                                        message.content?.reasoning,
                                                        `Score: ${formatFloat(message.content?.score ?? 0)}`,
                                                        `State: ${message.content?.state ?? "unknown"}`,
                                                        `Turn delta: ${formatFloat(message.content?.turn_delta ?? 0)}`,
                                                        `Conversation delta: ${formatFloat(message.content?.conversation_delta ?? 0)}`,
                                                        `Loss streak: ${message.content?.loss_streak ?? 0}`
                                                        + `/${message.content?.required_loss_streak ?? 0}`
                                                    ].filter(Boolean).join("\n\n")
                                                    : message.content,
                                                usage: {
                                                    prompt_tokens: message.usage?.prompt_tokens,
                                                    prompt_token_details: {
                                                        cached_tokens: message.usage?.cached_prompt_tokens
                                                    },
                                                    completion_tokens: message.usage?.completion_tokens
                                                },
                                                generated_at: message.timestamp,
                                                previousGeneratedAt: index > 0
                                                    ? run.value.conversation[index - 1].timestamp
                                                    : null
                                            })
                                        )
                                        : PlaygroundDefault(
                                            "chat-3-line",
                                            "No messages were recorded for this run."
                                        ),
                                run.value?.error && ConversationInfoMessage({
                                    variant: "error",
                                    icon: "error-warning-line",
                                    label: "Run error",
                                    content: run.value.error
                                })
                            )
                        )
                    )
                )
            )
        )
    );
}
